/**
 * Starting a connection (POST /v1/connections) and deciding what happens next.
 * Kept free of React so every branch is unit-tested.
 */
import { api, ApiError, errorMessage } from '@/api/client';
import type { Connection, Integration } from '@/api/types';
import { session } from '@/lib/storage';

export type ConnectOutcome =
  /** OAuth: the browser must go to the provider. */
  | { kind: 'redirect'; url: string }
  /** Created at once (demo mode, reused grant, PayPal, stocks) and nothing left to choose. */
  | { kind: 'connected'; connection: Connection }
  /** Created, but the user must pick the Page / ad account / property. */
  | { kind: 'needs_account'; connection: Connection }
  /** 409: partner_required, shown as "Coming soon" with an integration request. */
  | { kind: 'coming_soon'; message: string }
  /** 422: fix the form. */
  | { kind: 'invalid'; message: string; fields: Record<string, string> }
  | { kind: 'error'; message: string };

export interface ConnectInput {
  credentials?: Record<string, string>;
  fields?: Record<string, string>;
}

export const CALLBACK_PATH = '/connections/callback';

export function callbackUrl(origin: string = window.location.origin): string {
  return `${origin}${CALLBACK_PATH}`;
}

export async function startConnection(integration: Pick<Integration, 'id' | 'status' | 'name'>, input: ConnectInput = {}): Promise<ConnectOutcome> {
  if (integration.status === 'partner_required') {
    return { kind: 'coming_soon', message: `${integration.name} is coming soon.` };
  }
  try {
    const res = await api.post('/v1/connections', {
      body: {
        integration_id: integration.id,
        return_to: callbackUrl(),
        ...(input.credentials ? { credentials: input.credentials } : {}),
        ...(input.fields ? { fields: input.fields } : {}),
      },
    });
    if (res.authorize_url) return { kind: 'redirect', url: res.authorize_url };
    if (res.connection) {
      return res.connection.status === 'needs_account'
        ? { kind: 'needs_account', connection: res.connection }
        : { kind: 'connected', connection: res.connection };
    }
    return { kind: 'error', message: 'The server didn’t say how to continue. Please try again.' };
  } catch (e) {
    if (e instanceof ApiError && e.status === 409) return { kind: 'coming_soon', message: e.message };
    if (e instanceof ApiError && e.status === 422) return { kind: 'invalid', message: e.message, fields: e.fields };
    return { kind: 'error', message: errorMessage(e) };
  }
}

// ------------------------------------------------------------------ where to go after the round trip

const PENDING_KEY = 'mydash.connect.pending';

export interface PendingConnect {
  integration_id: string;
  /** Where to continue after connecting: '/onboarding' or '/'. */
  next: string;
}

export function rememberPending(p: PendingConnect): void {
  session.set(PENDING_KEY, p);
}

export function takePending(): PendingConnect | null {
  const p = session.get<PendingConnect | null>(PENDING_KEY, null);
  session.remove(PENDING_KEY);
  return p;
}

/** Overridable in tests (jsdom can't navigate). */
export const browser = {
  assign(url: string) {
    window.location.assign(url);
  },
};
